import React, { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import useApi from 'hooks/useApi'
import messages from './messages'
import { FormattedMessage, FormattedDate, FormattedTime, useIntl } from 'react-intl'
import { useKeyboardShortcut, useDebounce } from '@arnsbogroup/react-voicearchive/lib/hooks'
import useCurrentUser from 'hooks/useCurrentUser'
import {
  Avatar,
  Banner,
  Button,
  Card,
  Chip,
  DataTable,
  Icon,
  InputInline,
  MenuButton,
  Person,
  SelectInline,
  Table,
  TextArea,
  LoadingFull,
  DropdownMenu,
  SelectAsyncInline,
  MarkdownViewer,
  Field
} from '@arnsbogroup/react-voicearchive'
import { useParams } from 'react-router-dom'
import classNames from 'classnames'
import { Menu, Transition } from '@headlessui/react'
import Tabs from 'components/TabsRoutes'
import { useRouteMatch } from 'react-router-dom'
import NiceModal from '@ebay/nice-modal-react'
import DateTime from 'react-datetime'
import debounce from 'lodash.debounce'
import TaskFiles from './../TaskFiles'
import CopyLinkMenuItem from './../CopyLinkMenuItem'

import ReportIncidentModal from 'components/ReportIncidentModal'
import OrderMemoriesTab from 'components/OrderMemoriesTab'

import AddTaskSupplierDialog from './components/AddTaskSupplierDialog'
import ChangeTaskType from './components/ChangeTaskType'
import ApproveAllFiles from './components/ApproveAllFiles'
import InlineMediaSelect from 'components/InlineMediaSelect'
import OpenQualityCheckDialog from './components/OpenQualityCheckDialog'
import SetCurrentTaskSupplierApproval from './components/SetCurrentTaskSupplierApproval'
import ShowCustomerNoteDialog from './components/ShowCustomerNoteDialog'
import StartQualityCheck from './components/StartQualityCheck'
import UploadTaskFilesModal from 'components/UploadTaskFilesModal'
import AddOrderFilesToTaskModal from './components/AddOrderFilesToTaskModal'
import AutomationMenuFragments from './components/AutomationMenuFragments'
import ChangeSupplier from './components/ChangeSupplier'
import DuplicateTask from './components/DuplicateTask'
import ReportIncident from './components/ReportIncident'
import RenotifyClient from './components/RenotifyClient'
import MarkAsPaidMenuItem from './components/MarkAsPaidMenuItem'
import DeliverFiles from './components/DeliverFiles'
import NotifyProducerOfFilesReady from './components/NotifyProducerOfFilesReady'
import BookLsMeeting from './components/BookLsMeeting'
import DeleteTaskMenuItem from '../DeleteTaskMenuItem'
import DisableUsageReminder from './components/DisableUsageReminder'
import HistoryTab from './components/HistoryTab'
import MessagesTab from './components/MessagesTab'
import EventsWidget from './components/EventsWidget'
import AIVoiceLinesTab from './components/AIVoiceLinesTab'
import UsageStatusChip from 'components/UsageStatusChip'

NiceModal.register('ADD_TASK_SUPPLIER_DIALOG', AddTaskSupplierDialog)
NiceModal.register('APPROVE_ALL_FILES', ApproveAllFiles)
NiceModal.register('OPEN_QUALITY_CHECK_DIALOG', OpenQualityCheckDialog)
NiceModal.register('RETAKE_DIALOG', ReportIncidentModal)
NiceModal.register('SET_CURRENT_TASK_SUPPLIER_APPROVAL', SetCurrentTaskSupplierApproval)
NiceModal.register('SHOW_CUSTOMER_NOTE_DIALOG', ShowCustomerNoteDialog)
NiceModal.register('START_QUALITY_CHECK', StartQualityCheck)
// Task status action (ordertask_status.action): deliverables uploaded on behalf of the talent.
NiceModal.register('UPLOAD_TASK_FILE_DIALOG', UploadTaskFilesModal)

const TaskPage = ({ refetch, order, task: data, jobId, version }) => {
  const { taskId } = useParams()
  const { formatMessage } = useIntl()
  const { path, url } = useRouteMatch()
  const { roles = [] } = useCurrentUser()

  const [loading, setLoading] = useState(false)
  const invoiceNoteRef = useRef(null)
  const coordinatorNoteFocused = useRef(false)
  const invoiceNoteFocused = useRef(false)
  const [files, setFiles] = useState([])
  const [task, setTask] = useState(data || {})
  const [supplierUserIdCopied, setSupplierUserIdCopied] = useState(false)
  const debouncedTask = useDebounce(task, 1200)

  const [taskFiles, taskFilesLoading,, loadTaskFiles] = useApi(`/rest/v1/task/${taskId}/files`)
  const [jobFiles, jobFilesLoading,, loadJobFiles] = useApi(`/rest/v1/task/${jobId}/files`)
  const [currencies] = useApi(`/rest/v1/currency?search[hidden]=0&pageSize=999`)
  const currencyOptions = currencies?.map(({ uid, iso }) => ({ value: uid, label: iso })) || []

  const fetchFiles = useCallback(debounce(loadTaskFiles, 400), [])

  const readOnly = task?.readOnly || loading
  const supplier = typeof task.supplier !== 'string' ? task.supplier : null
  const preferredSupplier = typeof task.preferredSupplier !== 'string' ? task.preferredSupplier : null

  const [territoryMarketOptions] = useApi(`/rest/v2/territoryMarket?search[deleted]=0&pageSize=999`)
  const [usageOptions] = useApi(`/rest/v2/orderusage?pageSize=999`)

  useEffect(() => {
    setFiles([])
    loadTaskFiles(`/rest/v1/task/${taskId}/files`)
  }, [taskId])

  useEffect(() => loadJobFiles(`/rest/v1/task/${jobId}/files`), [jobId])

  useEffect(() => {
    setTask(prev => {
      const overrides = {}
      if (coordinatorNoteFocused.current) overrides.coordinatornote = prev.coordinatornote
      if (invoiceNoteFocused.current) overrides.invoice_note = prev.invoice_note
      return { ...data, ...overrides }
    })
  }, [data])
  useEffect(() => setFiles(taskFiles), [taskFiles])
  useEffect(() => {
    if (Object.entries(debouncedTask).some(([key, value]) => data[key] !== value)) {
      onPersist()
    }
  }, [debouncedTask])

  const onNextAction = useCallback(() => {
    if (task?.status?.action) {
      NiceModal.show(task.status.action, { order, task, version, files })
        .then((options) => {
          const { refetchFiles = false } = options || {}

          refetch()

          if (refetchFiles) {
            fetchFiles()
          }
        })
        .catch((error) => {
          console.error(error)
        })
    }
  }, [task, order])

  const onAddOrderFiles = useCallback(() => {
    NiceModal.show(AddOrderFilesToTaskModal, { order, task })
      .then(fetchFiles)
  }, [task, order])

  const onPersist = () => {
    setLoading(true)

    const body = {}

    Object.entries(task).map(([key, value]) => {
      if (data[key] !== value) {
        body[key] = value
      }
    })

    fetch(`/rest/v2/task/${task.uid}`, { method: 'PUT', body: JSON.stringify(body) })
      .then((response) => response.json())
      .then(({ success, data, meta, errors }) => {
        if (!success) {
          window?.toastr.error(Object.values(errors).join(', '))
        }

        if (meta && meta?.message) {
          toastr.info(meta?.message)
        }
      })
      .then(() => setLoading(false))
      .then(refetch)
      .catch((error) => window.alert(error))
  }

  const onSupplierUserIdCopy = () => {
    navigator.clipboard.writeText(supplier ? supplier?.userid : preferredSupplier?.userid)
    setSupplierUserIdCopied(true)
    toastr.success(formatMessage(messages.supplierUserIdCopiedSuccess))
  }

  const allFiles = [
    ...(files || []),
    ...(jobFiles || [])
  ]

  const getDeadlineWarningSubtitle = () => {
    const isAfterOrder = task.deadline > order.deadline
    const prTask = [...version.tasks].reverse().find((task) => task?.type?.shortname === 'AP' && !task?.retakecreated && task?.supplier?.uid)
    const isAfterPR = task.deadline > prTask?.deadline

    if (isAfterOrder && isAfterPR) {
      return formatMessage(messages.taskDeadlineAfterOrderAndPr)
    }
    if (isAfterOrder) {
      return formatMessage(messages.taskDeadlineAfterOrder)
    }
    if (isAfterPR) {
      return formatMessage(messages.taskDeadlineAfterPr)
    }
    return ''
  }

  return (
    <div className="h-full w-full flex flex-col overflow-auto bg-white">
      <div className="shrink-0 w-full p-4 lg:flex lg:items-center lg:justify-between shrink-0">
        <div className="min-w-0 flex text-sm">
          { supplier ? (
            <a href={`/suppliers/${supplier.uid}`} target="_blank" className="cursor-pointer">
              <Person
                name={supplier.userid}
                image={supplier.image}
                email={supplier.email}
              />
            </a>
          ) : preferredSupplier ? (
            <a href={`/suppliers/${preferredSupplier.uid}`} target="_blank" className="cursor-pointer">
              <Person
                name={preferredSupplier.userid}
                image={preferredSupplier.image}
                email={preferredSupplier.email}
                className="opacity-50"
                title="Preferred supplier"
                chip={<Chip state="warning">Preferred</Chip>}
              />
            </a>
          ) : (
            <Person
              className="opacity-25"
              title="Unassigned"
              name="Unassigned"
            />
          ) }
          {(supplier || preferredSupplier) &&
            <Icon 
              className="cursor-pointer ml-2 my-auto text-gray-400 group-hover:text-primary"
              icon={["fas", supplierUserIdCopied ? "check" : "copy"]} 
              onClick={() => onSupplierUserIdCopy()}
            />
          }
        </div>

        <div className="mt-5 flex lg:mt-0 lg:ml-4 shrink-0">
          { loading ? (
            <Button disabled size="sm" variant="text">
              Saving changes <Icon icon={['fal', 'spinner-third']} className="animate-spin" />
            </Button>
          ) : (
            <>
              <span className="hidden sm:block">
                <Button
                  size="sm"
                  variant="text"
                  icon={['fal', +task.removeFromActionList ? 'plus' : 'check']}
                  className={+task.removeFromActionList ? 'text-gray-400 hover:text-primary': 'text-primary' }
                  iconPosition="right"
                  onClick={() => setTask({ ...task, removeFromActionList: +task.removeFromActionList ? 0 : 1 })}
                >
                  { +task.removeFromActionList ? 'Show in Action Tasks' : 'Hide in Action Tasks' }
                </Button>
              </span>

              { !readOnly && !!task?.status?.action &&
                <span className="sm:ml-3">
                  <Button size="sm" onClick={onNextAction}>
                    { task?.status?.commandtext }
                  </Button>
                </span>
              }

              <Menu as="div" className="relative ml-3">
                <Menu.Button>
                  <Button variant="outline" size="sm" icon={['fal', 'ellipsis-vertical']} iconPosition="right">
                    More
                  </Button>
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-50 mt-2 -mr-1 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <CopyLinkMenuItem />
                    <DuplicateTask readOnly={readOnly} task={task} refetch={refetch} />
                    <ReportIncident task={task} refetch={refetch} />
                    <ChangeSupplier readOnly={readOnly} task={task} order={order} files={files} refetch={refetch} version={version} />
                    { ['LSA', 'LSP'].includes(task?.type?.shortname) &&
                      <BookLsMeeting readOnly={readOnly} task={task} order={order} refetch={refetch} />
                    }
                    <RenotifyClient readOnly={readOnly} task={task} refetch={refetch} />
                    <DeliverFiles readOnly={readOnly} task={task} refetch={refetch} />
                    {task?.type?.shortname == "AP" && task?.supplier?.uid &&
                      <NotifyProducerOfFilesReady readOnly={readOnly} task={task} refetch={refetch} />
                    }
                    <AutomationMenuFragments
                      readOnly={readOnly}
                      taskId={task.uid}
                    />
                    <DisableUsageReminder task={task} refetch={refetch} readOnly={readOnly} />
                    <DeleteTaskMenuItem taskType="Task" readOnly={readOnly} task={task} order={order} refetch={refetch} />
                    <ChangeTaskType readOnly={readOnly} task={task} refetch={refetch} />
                    {roles.includes('finance') &&
                      <MarkAsPaidMenuItem
                        task={task}
                        onMarkAsPaid={(data) => setTask({ ...task, isPaid: !task.isPaid })}
                      />
                    }
                  </Menu.Items>
                </Transition>
              </Menu>
            </>
          )}
        </div>
      </div>

      <div className="shrink-0 w-full px-4 pb-2 lg:flex lg:items-center lg:justify-between shrink-0">
        <div className="flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6 pt-2">
          <Chip>{ task.type?.name }</Chip>
          {!!task?.on_hold &&
            <Chip state="error"><FormattedMessage {...messages.onHold} /></Chip>
          }

          <div className="flex items-center text-sm text-gray-500" title={formatMessage(messages.taskId)}>
            <Icon icon={['fas', 'hashtag']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />
            { task.uid }
          </div>

          <div className="flex items-center text-sm text-gray-500" title={formatMessage(messages.cost)}>
            <Icon icon={['fas', 'dollar']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />

            <div className="flex space-x-1">
              <InputInline
                disabled={readOnly || (task.isPaid && !(roles.includes('finance') || roles.includes('finance_partial')))}
                type="number"
                min={0}
                value={task.cost || 0}
                onChange={({ target: { value } }) => setTask({ ...task, cost: value })}
              />

              <SelectInline
                disabled={readOnly || !(roles.includes('finance') || roles.includes('finance_partial'))}
                value={currencyOptions.find(({ value }) => value == task.costcurrencyId)}
                options={currencyOptions}
                placeholder="ISO"
                onChange={({ value, label }) => setTask({ ...task, costcurrency: value, costcurrencyId: value })}
              />
            </div>
          </div>

          <div className="flex items-center text-sm text-gray-500" title={formatMessage(messages.price)}>
            <Icon icon={['fas', 'tag']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />

            <div className="flex space-x-1">
              <InputInline
                disabled={readOnly}
                type="number"
                min={0}
                value={task.price || 0}
                onChange={({ target: { value } }) => setTask({ ...task, price: value })}
              />
              <SelectInline
                disabled={readOnly || !(roles.includes('finance') || roles.includes('finance_partial'))}
                value={currencyOptions.find(({ value }) => value == task.pricecurrencyId)}
                options={currencyOptions}
                placeholder="ISO"
                onChange={({ value }) => setTask({ ...task, pricecurrency: value, pricecurrencyId: value })}
              />
            </div>
          </div>

          <div className="flex items-center text-sm text-gray-500" title={formatMessage(messages.discount)}>
            <Icon icon={['fas', 'gift']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />

            <div className="flex space-x-1">
              <InputInline
                disabled={readOnly}
                type="number"
                max={100}
                min={0}
                value={task.discount}
                onChange={({ target: { value } }) => setTask({ ...task, discount: value })}
              />
              <span>%</span>
            </div>
          </div>

          <div className="flex items-center text-sm text-gray-500" title={formatMessage(messages.priceNet)}>
            <Icon icon={['fas', 'building-columns']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />
            Net { task.stats.priceNet } { task.stats.currency }
          </div>

          <div className="flex items-center text-sm text-gray-500" title={formatMessage(messages.grossProfit)}>
            <Icon
              icon={['fas', 'arrow-trend-up']}
              className={classNames('mr-1.5 shrink-0', task.stats.grossProfitPct > 55 ? 'text-positive' : 'text-negative')}
            />
            GM { task.stats.grossProfit } { task.stats.currency } ({ task.stats.grossProfitPct }%)
          </div>

          <div className="flex items-center text-sm text-gray-500" title={formatMessage(messages.deadline)}>
            <Icon icon={['fas', 'calendar']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />

            <DateTime
              disabled={readOnly}
              value={new Date(+task.deadline * 1000)}
              inputProps={{
                className: classNames('bg-transparent', {
                  'border-b border-dashed border-gray-300 cursor-pointer hover:border-gray-500': !readOnly
                })
              }}
              onChange={(value) => setTask({ ...task, deadline: Math.round(new Date(value).getTime() / 1000) })}
              closeOnTab
            />
          </div>
          <div className="flex items-center text-sm text-gray-500" title={formatMessage(messages.endCustomer)}>
            <Icon icon={['fas', 'building']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />

            <InputInline
              disabled={readOnly}
              type="text"
              placeholder="End Client"
              value={task.endCustomer}
              onChange={({ target: { value } }) => setTask({ ...task, endCustomer: value })}
            />
          </div>

          <div className="flex items-center text-sm text-gray-500" title={formatMessage(messages.po)}>
            <Icon icon={['fas', 'receipt']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />

            <InputInline
              disabled={readOnly}
              type="text"
              placeholder="P.O."
              value={task.customerReference}
              onChange={({ target: { value } }) => {
                if (value.trim().split(/\s+/).filter(Boolean).length > 3) {
                  toastr.info('P.O. should be max 3 words. Use the Invoice Note field for longer descriptions.')
                  invoiceNoteRef.current?.focus()
                }
                const trimmedValue = value
                  .trim()
                  .split(/\s+/)
                  .slice(0, 3)
                  .join(' ')
                setTask({ ...task, customerReference: trimmedValue })
              }}
            />
          </div>

          <div className="flex items-center text-sm text-gray-500" title={formatMessage(messages.wordCount)}>
            <Icon icon={['fas', 'file-alt']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />

            <InputInline
              disabled={readOnly}
              type="number"
              placeholder={formatMessage(messages.wordCount)}
              value={task?.wordcount}
              onChange={({ target: { value } }) => setTask({ ...task, wordcount: value })}
            />
          </div>
        </div>
      </div>

      <div className="grow w-full h-full overflow-auto">
        { (!!task.isInvoiced || !!task.isPaid) && roles.includes('finance') &&
          <Banner
            state={+task.isPaid ? "error" : "warning"}
            title="Danger!"
            subtitle={`This task is already ${+task.isPaid ? "paid" : "invoiced"}. You have finance super powers to edit, but please be cautious about any changes you make.`}
          />
        }

        {
          (
            (task.deadline > order.deadline && !task?.retakecreated && task?.supplier?.uid) ||
            task.deadline > [...version.tasks].reverse().find((task) => task?.type?.shortname === 'AP' && !task?.retakecreated && task?.supplier?.uid)?.deadline
          ) && (
            <Banner
              state="warning"
              title="Deadline update reminder"
              subtitle={getDeadlineWarningSubtitle()}
            />
          )
        }

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 p-4">
          <Card className="p-0 bg-yellow-100 border-yellow-300 text-xs text-gray-700">
            <TextArea
              className="bg-yellow-100 p-4 border-0 text-xs text-gray-700 whitespace-pre-wrap font-mono"
              rows={12}
              value={task.coordinatornote || ''}
              placeholder="Coordinator Note"
              onChange={({ target: { value } }) => setTask({ ...task, coordinatornote: value })}
              onFocus={() => { coordinatorNoteFocused.current = true }}
              onBlur={() => { coordinatorNoteFocused.current = false }}
            />

            { !!task?.internalnote.length &&
              <TextArea
                disabled
                className="bg-yellow-100 p-4 border-0 text-xs text-gray-700 whitespace-pre-wrap font-mono"
                rows={6}
                value={task.internalnote || ''}
                placeholder="Internal Note"
              />
            }
          </Card>

          <Card className="bg-white p-4 text-xs text-gray-700 whitespace-pre-wrap break-words overflow-x-auto font-mono" title="External Note">
            <MarkdownViewer>
              {task.externalnote || '-'}
            </MarkdownViewer>
          </Card>

          <div>
            { order?.awaitPronunciation > 0 &&
              <Banner state="warning" title="Await pronunciation from client" />
            }

            { !!task?.customerDeclinedReason &&
              <>
                <Banner state="error" title="Declined" subtitle={task.customerDeclinedReason.name} />

                { !!task?.customerDeclinedNote?.length &&
                  <p
                    className="p-4 bg-red-50 text-xs font-mono whitespace-pre-wrap overflow-scroll"
                    title="Declined Note"
                  >
                    { task.customerDeclinedNote }
                  </p>
                }
              </>
            }

            { !!task?.retakeReason &&
              <>
                <Banner state="error" title="Retake" subtitle={task.retakereason.name} />

                { !!task?.retakeReasonNote?.length &&
                  <p
                    className="p-4 bg-red-50 text-xs font-mono whitespace-pre-wrap overflow-scroll"
                    title="Retake Note"
                  >
                    { task.retakeReasonNote }
                  </p>
                }
              </>
            }

            { !!task?.declinedreason && task?.declinedreason != '' &&
              <>
                <Banner state="error" title="Rejected" subtitle="Customer rejected the recording with reason:" />
                <p
                  className="p-4 bg-red-50 text-xs font-mono whitespace-pre-wrap"
                  title="Retake Note"
                >
                  { task.declinedreason }
                </p>
              </>
            }

            <EventsWidget task={task} />
          </div>

          <Card className="p-4 border-yellow-300 text-sm text-gray-700 space-y-4">
            <TextArea
              innerRef={invoiceNoteRef}
              disabled={task?.readOnly}
              className="p-0 border-0 text-xs text-gray-700 font-mono"
              rows={8}
              value={task.invoice_note || ''}
              placeholder="Any text entered here will appear on the task invoice line..."
              onChange={({ target: { value } }) => setTask({ ...task, invoice_note: value })}
              onFocus={() => { invoiceNoteFocused.current = true }}
              onBlur={() => { invoiceNoteFocused.current = false }}
            />
          </Card>

        </div>
          <div
            className={classNames('grid gap-6 p-4', !!task.fileSpecs.length ? 'grid-cols-2' : 'grid-cols-1')}
          >
            {(task.type?.shortname == 'VO' || task.type?.shortname == 'USAGE') &&
              <div>
                <div className="text-md font-bold space-x-1 flex">
                    <label className="block text-gray-700">
                      <FormattedMessage {...messages.usageRights} />
                    </label>
                    <div>
                      <UsageStatusChip
                        task={task}
                        reminderDisabled={!!(+order?.usage_reminder_disabled) || !!(+task?.usage_reminder_disabled)}
                      />
                    </div>
                </div>
                <div
                  className={classNames('grid gap-2', task.type?.shortname == 'USAGE' ? 'grid-cols-4' : 'grid-cols-3')}
                >
                  <InlineMediaSelect
                    value={task.medias}
                    onChange={(medias) => setTask({ ...task, medias })}
                    disabled={readOnly}
                  />
                  <SelectInline
                    disabled={readOnly}
                    value={!task?.territoryMarkets ? null : task.territoryMarkets.map(({ uid, name }) => ({
                      value: uid,
                      label: name,
                    })).filter((territoryMarket) => territoryMarket.value)}
                    onChange={(values) => setTask({
                      ...task,
                      territoryMarkets: !values ? [] : values.map(({ label, value }) => ({
                        name: label,
                        uid: value,
                      }))
                    })}
                    placeholder={formatMessage(messages.territoryMarketPlaceholder)}
                    label={formatMessage(messages.territoryMarketPlaceholder)}
                    isClearable
                    isMulti
                    cacheOptions
                    defaultOptions
                    options={territoryMarketOptions?.map(({ uid, name }) => ({ value: +uid, label: name }))}
                  />
                  <SelectInline
                    disabled={readOnly}
                    value={!task.usage ? null : {label: task.usage.name, value: task.usage.uid}}
                    onChange={(value) => setTask({
                      ...task,
                      usage: {
                        name: value?.label,
                        uid: value?.value
                      }
                    })}
                    placeholder={formatMessage(messages.usagePlaceholder)}
                    label={formatMessage(messages.usagePlaceholder)}
                    isClearable
                    cacheOptions
                    defaultOptions
                    options={usageOptions?.map(({ uid, name }) => ({ value: +uid, label: name }))}
                  />
                  {task.type?.shortname == 'USAGE' &&
                    <Field label={formatMessage(messages.airingTimeStart)}>
                      <DateTime
                        key={`${taskId}-${task?.airing_time_start ?? 'empty'}`}
                        disabled={readOnly}
                        value={
                          task?.airing_time_start
                            ? new Date(task.airing_time_start * 1000)
                            : null
                        }
                        onChange={(date) => {
                          if (readOnly) return;

                          const newValue = date
                            ? date.startOf('day').unix()
                            : null;

                          setTask({
                            ...task,
                            airing_time_start: newValue
                          });
                        }}
                        timeFormat={false}
                        inputProps={{
                          placeholder: formatMessage(messages.airingTimeStart),
                          className: classNames('bg-transparent w-1/3', {
                            'border-b border-dashed border-gray-300 cursor-pointer hover:border-gray-500': !readOnly
                          })
                        }}
                      />
                    </Field>
                  }
                </div>

              </div>
            }
            {!!task.fileSpecs.length &&
              <div>
                <div className="text-sm font-medium space-x-1 flex">
                    <label className="block text-gray-700">
                      <FormattedMessage {...messages.fileSpecLabel} />
                    </label>
                </div>
                <div className='flex gap-2 flex-wrap'>
                  {task.fileSpecs.map((fileSpec) =>
                    <Chip>
                      {fileSpec?.name}
                    </Chip>
                  )}
                </div>
              </div>
            }
          </div>
        <Tabs
          className="w-full"
          childrenClassName="bg-white"
          tabsClassName="px-4"
          tabs={[
            (task?.type?.shortname === 'Ai') ? {
              message: 'AI Voice Lines',
              to: `${url}/ai-voice-lines`,
              route: {
                path: `${path}/ai-voice-lines`,
                render: (props) => <AIVoiceLinesTab task={task} {...props} />
              }
            } : null,
            {
              message: "Files",
              to: `${url}/files`,
              chip: <Chip>{ allFiles.length }</Chip>,
              route: {
                path: `${path}/files`,
                render: (props) => (
                  <div>
                    <TaskFiles
                      fetchFiles={fetchFiles}
                      loading={taskFilesLoading || jobFilesLoading}
                      files={allFiles}
                      setFiles={setFiles}
                      task={task}
                      {...props}
                    />

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onAddOrderFiles}
                      disabled={!order?.deal_id}
                    >
                      Attach from deal { order.deal_id }
                    </Button>
                  </div>
                ),
              },
            },
            {
              message: 'History',
              to: `${url}/history`,
              route: {
                path: `${path}/history`,
                component: HistoryTab,
              }
            },
            {
              message: 'Messages',
              to: `${url}/messages`,
              route: {
                path: `${path}/messages`,
                render: (props) => <MessagesTab task={task} order={order} {...props } />,
              }
            },
            {
              message: 'Memories',
              to: `${url}/memories`,
              route: {
                path: `${path}/memories`,
                render: () => (
                  <OrderMemoriesTab
                    order={order}
                    contextType="task"
                    contextUid={task.uid}
                    version={version}
                  />
                ),
              },
            },
          ]}
        />
      </div>
    </div>
  )
}

export default ({ task, ...rest }) => !task ? (
  <LoadingFull />
) : (
  <TaskPage task={task} {...rest} />
)
